/*
  File: AgentCard.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the AgentCard component, which displays detailed information about an agent.
  It renders the agent's name, status, email, phone number, referral details, and network statistics.

  Components:
  - AgentCard: Main component for displaying agent information.
  - AgentPathComponent: Component for rendering the network path of the agent.

  Props:
  - agentInstance: The agent instance containing data about the agent to be displayed.

  Dependencies:
  - antd: Provides components like Tag and Avatar for UI elements.
  - framer-motion: Library for animations.

  Styles:
  - AgentCard.css: Contains styles for the AgentCard component.

*/

import React from "react";
import { Tag } from "antd";
import { AgentInstance, AgentTree } from "./types";
import AgentPathComponent from "./AgentPath";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import "./AgentCard.css";

interface AgentCardProps {
  agentInstance: AgentInstance | null;
}
function getTotalSubArrayLength(tree: AgentTree): number {
  let totalLength = 0;

  // Iterate over each key-value pair in the tree
  for (const [key, value] of Object.entries(tree)) {
    // Check if the current value is an array (sub-array)
    if (Array.isArray(value)) {
      // Add the length of the current sub-array to the total
      totalLength += value.length;
    } else {
      console.warn(
        `Encountered non-array value for key "${key}" in tree. Skipping.`
      );
    }
  }

  return totalLength - 1;
}

const AgentCard: React.FC<AgentCardProps> = ({ agentInstance }) => {
  const formatPhoneNumber = (phoneNumber: string) => {
    const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, "US");
    if (phoneNumberObj) {
      // Format the phone number as (XXX) XXX-XXXX
      return phoneNumberObj.formatNational();
    }
    return phoneNumber;
  };

  if (!agentInstance) {
    return <div>Loading...</div>;
  }

  const { agent } = agentInstance;

  if (!agent) {
    return <div>No data available</div>;
  }

  return (
    <div className="agent-card">
      <div className="agent-info">
        <div className="agent-name">
          {agent.first_name} {agent.last_name}
        </div>

        <div>
          <Tag
            color={agent.status === "active" ? "green" : "red"}
            className="agent-status"
          >
            {agent.status.toUpperCase()}
          </Tag>
        </div>
      </div>
      <div className="agent-details">
        <div className="agent-details-column">
          <div className="agent-label">Email:</div>
          <span>{agent?.email}</span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">Phone Number:</div>
          <span>{agent ? formatPhoneNumber(agent.phone_number) : ""}</span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">Referred By:</div>
          <span>
            {agentInstance?.agent.referred_by
              ? `${agentInstance.agent.referred_by.first_name} ${agentInstance.agent.referred_by.last_name}`
              : "House Account"}
          </span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">Referred Date:</div>
          <span>
            {agent && new Date(agent.referred_date).toLocaleDateString()}
          </span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">My Passive Income Network:</div>
          <span>{getTotalSubArrayLength(agentInstance?.tree)}</span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">My Direct Referrals:</div>
          <span>{agentInstance?.agent.referrals?.length}</span>
        </div>

        {/* <div className="agent-details-column">
          <div className="agent-label">Network Level:</div>
          <span>{JSON.stringify(agentInstance?.paths.ancestors.length)}</span>
        </div> */}
      </div>
      {/* <div>
        <div className="agent-details-column">
          <div className="agent-label">Network Path:</div>
          <span>
            <AgentPathComponent ancestors={agentInstance?.paths.ancestors} />
          </span>
        </div>
      </div> */}
    </div>
  );
};

export default AgentCard;
